import { supabase } from '@/integrations/supabase/client';
import { SearchBuilder } from '../core/SearchBuilder';
import { SearchFilters, SearchResult } from '../core/types';
import { LocationService } from '../../locationService';

export class DisplaySearch extends SearchBuilder {
  constructor(filters: SearchFilters) {
    super(filters);
  }

  protected buildBaseQuery() {
    return supabase
      .from('displays')
      .select(`
        id,
        name,
        description,
        location,
        latitude,
        longitude,
        holiday_type,
        display_type,
        average_rating,
        year_started,
        image_url,
        created_at,
        display_years(
          id,
          display_media(id)
        )
      `);
  }

  /**
   * Apply display-specific filters
   */
  private applyDisplayFilters(query: ReturnType<typeof supabase.from>): ReturnType<typeof supabase.from> {
    // Apply display type filters
    if (this.filters.displayTypes && this.filters.displayTypes.length > 0) {
      query = query.in('display_type', this.filters.displayTypes);
    }

    // Apply year filters
    if (this.filters.yearStarted?.min !== undefined) {
      query = query.gte('year_started', this.filters.yearStarted.min);
    }
    if (this.filters.yearStarted?.max !== undefined) {
      query = query.lte('year_started', this.filters.yearStarted.max);
    }

    return query;
  }

  /**
   * Handle location-based searches using PostGIS
   */
  private async handleLocationSearch(): Promise<SearchResult[]> {
    if (this.filters.location && this.filters.radius) {
      // Use radius-based PostGIS search
      const spatialResults = await LocationService.findDisplaysWithinRadius(
        this.filters.location,
        {
          radius: this.filters.radius,
          limit: this.filters.limit || 100,
          minRating: this.filters.minRating,
          holidayTypes: this.filters.holidayTypes,
          displayTypes: this.filters.displayTypes,
          sortBy: 'distance'
        }
      );

      // Transform spatial results to SearchResult format
      let results: SearchResult[] = spatialResults.map((display) => ({
        id: display.id,
        type: 'display' as const,
        title: display.name,
        description: `Holiday light display in ${display.location}`,
        imageUrl: '',
        rating: display.rating,
        location: display.location,
        distance: display.distance_meters,
        holidayType: display.holiday_type || undefined,
        displayType: display.display_type || undefined,
        yearStarted: display.year_started,
        createdAt: new Date().toISOString(),
        relevanceScore: this.calculateSpatialRelevanceScore(display)
      }));

      // Apply additional text filtering if query is provided
      if (this.filters.query) {
        const query = this.filters.query.toLowerCase();
        results = results.filter(result =>
          result.title.toLowerCase().includes(query) ||
          result.description?.toLowerCase().includes(query) ||
          result.location?.toLowerCase().includes(query)
        );
      }

      return results;
    }

    if (this.filters.locationText) {
      const textResults = await LocationService.searchDisplaysByLocation(
        this.filters.locationText,
        this.filters.radius || 50000 // Default 50km radius
      );

      // Transform text search results
      const results: SearchResult[] = textResults.map((display) => ({
        id: display.id,
        type: 'display' as const,
        title: display.name,
        description: `Holiday light display in ${display.location}`,
        imageUrl: '',
        rating: display.rating,
        location: display.location,
        distance: display.distance_meters,
        holidayType: display.holiday_type || undefined,
        displayType: display.display_type || undefined,
        yearStarted: display.year_started,
        createdAt: new Date().toISOString(),
        relevanceScore: this.calculateSpatialRelevanceScore(display)
      }));

      return this.applyPostFilters(results);
    }

    return [];
  }

  /**
   * Apply filters after location search
   */
  private applyPostFilters(results: SearchResult[]): SearchResult[] {
    let filteredResults = results;

    // Apply additional filters
    if (this.filters.query) {
      const query = this.filters.query.toLowerCase();
      filteredResults = filteredResults.filter(result =>
        result.title.toLowerCase().includes(query) ||
        result.description?.toLowerCase().includes(query)
      );
    }

    if (this.filters.holidayTypes && this.filters.holidayTypes.length > 0) {
      filteredResults = filteredResults.filter(result =>
        result.holidayType && this.filters.holidayTypes!.includes(result.holidayType)
      );
    }

    if (this.filters.displayTypes && this.filters.displayTypes.length > 0) {
      filteredResults = filteredResults.filter(result =>
        result.displayType && this.filters.displayTypes!.includes(result.displayType)
      );
    }

    if (this.filters.minRating !== undefined) {
      filteredResults = filteredResults.filter(result => result.rating >= this.filters.minRating!);
    }

    if (this.filters.maxRating !== undefined) {
      filteredResults = filteredResults.filter(result => result.rating <= this.filters.maxRating!);
    }

    return filteredResults;
  }

  /**
   * Handle regular database searches
   */
  private async handleRegularSearch(): Promise<SearchResult[]> {
    let query = this.buildBaseQuery();

    // Apply all filters
    query = this.applyTextSearch(query);
    query = this.applyHolidayFilters(query);
    query = this.applyDisplayFilters(query);
    query = this.applyRatingFilters(query);
    query = this.applySorting(query);
    query = this.applyPagination(query);

    const { data, error } = await query;

    if (error) {
      console.error('Display search error:', error);
      throw new Error(`Display search failed: ${error.message}`);
    }

    return this.transformDisplayResults(data || []);
  }

  /**
   * Transform database results to SearchResult format
   */
  private transformDisplayResults(displays: Record<string, unknown>[]): SearchResult[] {
    return displays.map((display) => {
      const result: SearchResult = {
        id: display.id as string,
        type: 'display',
        title: display.name as string,
        description: display.description as string || `Holiday light display in ${display.location}`,
        imageUrl: display.image_url as string || '',
        rating: (display.average_rating as number) || 0,
        location: display.location as string,
        holidayType: display.holiday_type as string,
        displayType: display.display_type as string,
        yearStarted: display.year_started as number,
        createdAt: display.created_at as string,
        relevanceScore: this.calculateRelevanceScore(display)
      };

      // Calculate distance if user location is provided
      if (this.filters.location && display.latitude && display.longitude) {
        result.distance = this.calculateDistance(
          this.filters.location.lat,
          this.filters.location.lng,
          display.latitude as number,
          display.longitude as number
        );
      }

      return result;
    });
  }

  /**
   * Calculate spatial relevance score for location-based results
   */
  private calculateSpatialRelevanceScore(display: Record<string, unknown>): number {
    let score = 0;
    
    // Base score from rating
    const rating = (display.rating as number) || 0;
    score += rating * 20; // Max 100 points from rating
    
    // Distance penalty (closer is better)
    const distance = (display.distance_meters as number) || 0;
    const maxDistance = this.filters.radius || 50000;
    const distanceScore = Math.max(0, (maxDistance - distance) / maxDistance * 50);
    score += distanceScore;
    
    // Holiday type match boost
    if (this.filters.holidayTypes && display.holiday_type) {
      if (this.filters.holidayTypes.includes(display.holiday_type as string)) {
        score += 30;
      }
    }
    
    return Math.round(score);
  }

  /**
   * Execute the display search
   */
  public async execute(): Promise<SearchResult[]> {
    try {
      // Check if this is a location-based search
      if (this.filters.location || this.filters.locationText) {
        return await this.handleLocationSearch();
      }

      // Fallback to regular database search
      return await this.handleRegularSearch();
    } catch (error) {
      console.error('Display search execution error:', error);
      throw error;
    }
  }
}